import { useState } from 'react';
import { Link } from 'react-router-dom';
import { TESTIMONIALS, COMPANY } from '../../mocks/siteData';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

interface TestimonialsSectionProps {
  className?: string;
  title?: string;
}

export default function TestimonialsSection({
  className = '',
  title = 'What Homeowners Are Saying',
}: TestimonialsSectionProps) {
  const { ref, visible } = useScrollAnimation();
  const [active, setActive] = useState(0);

  return (
    <section className={`bg-[#0D0C0B] py-32 px-6 lg:px-16 xl:px-24 ${className}`}>
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-8 mb-16">
          <div>
            <p className="text-accent text-xs font-semibold tracking-[0.2em] uppercase mb-4">Client Testimonials</p>
            <h2 className="font-serif text-4xl lg:text-[52px] text-white leading-tight" style={{ letterSpacing: '-0.02em' }}>
              {title}
            </h2>
          </div>
          {/* Rating pill */}
          <div className="flex items-center gap-3 border border-stone-700/60 px-5 py-3 rounded-full flex-shrink-0">
            <div className="flex items-center gap-0.5">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="w-3 h-3 flex items-center justify-center">
                  <i className="ri-star-fill text-accent text-xs" />
                </div>
              ))}
            </div>
            <div className="w-px h-4 bg-stone-700" />
            <span className="text-white font-bold text-sm">{COMPANY.rating}</span>
            <span className="text-stone-500 text-xs">· {COMPANY.reviewCount} Reviews</span>
          </div>
        </div>

        {/* Main testimonial */}
        <div
          ref={ref}
          className={`transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          {/* Large featured testimonial */}
          <div className="relative border border-stone-800/60 rounded-2xl p-8 lg:p-12 mb-4 bg-stone-900/40">
            {/* Large quotation mark */}
            <div className="absolute top-6 right-8 text-stone-800 font-serif text-[100px] leading-none select-none hidden lg:block">&ldquo;</div>

            <p className="font-serif text-xl lg:text-2xl xl:text-3xl text-white leading-[1.4] mb-8 max-w-4xl relative z-10">
              &ldquo;{TESTIMONIALS[active].text}&rdquo;
            </p>

            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-accent font-bold text-base font-sans">{TESTIMONIALS[active].author.charAt(0)}</span>
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">{TESTIMONIALS[active].author}</p>
                  <p className="text-stone-500 text-xs">{TESTIMONIALS[active].location} &nbsp;·&nbsp; {TESTIMONIALS[active].service}</p>
                </div>
              </div>
              <div className="flex items-center gap-0.5">
                {[...Array(TESTIMONIALS[active].rating)].map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className="ri-star-fill text-accent text-xs" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation row */}
          <div className="flex items-center justify-between mb-10">
            <div className="flex items-center gap-1.5">
              {TESTIMONIALS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                    i === active ? 'bg-accent w-8' : 'bg-stone-700 hover:bg-stone-600 w-3'
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActive((a) => (a - 1 + TESTIMONIALS.length) % TESTIMONIALS.length)}
                className="w-9 h-9 rounded-full border border-stone-700 hover:border-accent flex items-center justify-center transition-colors cursor-pointer group"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-stone-500 group-hover:text-accent text-xs transition-colors" />
                </div>
              </button>
              <button
                onClick={() => setActive((a) => (a + 1) % TESTIMONIALS.length)}
                className="w-9 h-9 rounded-full border border-stone-700 hover:border-accent flex items-center justify-center transition-colors cursor-pointer group"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-stone-500 group-hover:text-accent text-xs transition-colors" />
                </div>
              </button>
            </div>
          </div>

          {/* Mini testimonial cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TESTIMONIALS.slice(0, 3).map((t, i) => (
              <button
                key={t.id}
                onClick={() => setActive(i)}
                className={`text-left p-5 rounded-xl border transition-all duration-200 cursor-pointer ${
                  active === i
                    ? 'border-accent/40 bg-stone-900/60'
                    : 'border-stone-800/50 bg-stone-900/20 hover:border-stone-700'
                }`}
              >
                <div className="flex items-center gap-0.5 mb-3">
                  {[...Array(5)].map((_, si) => (
                    <div key={si} className="w-2.5 h-2.5 flex items-center justify-center">
                      <i className="ri-star-fill text-accent text-[10px]" />
                    </div>
                  ))}
                </div>
                <p className="text-stone-400 text-xs leading-relaxed mb-3 line-clamp-2">&ldquo;{t.text}&rdquo;</p>
                <p className="text-stone-500 text-xs">{t.author} · {t.location}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Post-testimonial CTA */}
        <div className="mt-14 pt-12 border-t border-stone-800/60 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <p className="text-white font-semibold text-base mb-1">Join {COMPANY.reviewCount}+ satisfied homeowners</p>
            <p className="text-stone-500 text-sm">Brandon, Riverview, Tampa, Valrico &amp; surrounding areas</p>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-accent hover:bg-accent-dark text-white font-semibold px-7 py-3.5 rounded-lg transition-colors whitespace-nowrap group text-sm"
          >
            Start Your Project
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1 text-xs" />
            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
